import { FC } from 'react';
import { useMatch } from 'react-router';
import { StreamCardFragmentFragment } from '@/__generated__/graphql.ts';
import { CopyButton } from '@/components/copy-button/copy-button.tsx';
import { LayoutCard } from '@/components/layout/card.tsx';
import { FeaturedBadge } from '@/components/stream-list/stream-card/featured-badge.tsx';
import { StreamStateIndicator } from '@/components/stream-list/stream-state-indicator.tsx';
import { StreamAccessTill } from '@/components/streams/stream-access-till/stream-access-till.tsx';
import { StreamAuthor } from '@/components/streams/stream-author/stream-author.tsx';
import { getStreamCategoryTitle } from '@/components/streams/stream-categories.ts';
import { StreamFollowers } from '@/components/streams/stream-followers/stream-followers.tsx';
import { StreamPriceTag } from '@/components/streams/stream-tag/stream-price-tag.tsx';
import { StreamStateTag } from '@/components/streams/stream-tag/stream-state-tag.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import { getMonthlyPrice } from '@/utils/paid-data.ts';
import {
  cardDescription,
  cardTitle,
  categoryTag,
  copyIdButton,
  mobileHeaderMainContent,
  stateContainer,
  streamCard,
  streamCardFooter,
  streamCardFooterAuditory,
  streamCardHeader,
  streamCardLink,
  streamCardUsernameContainer,
  streamTag,
} from './stream-card.css.ts';

type StreamCardProps = {
  stream: StreamCardFragmentFragment;
};
export const StreamCardMobile: FC<StreamCardProps> = ({ stream }) => {
  const isMyStreams = useMatch(paths.streamsCreated);

  return (
    <LayoutCard className={streamCard}>
      <Link
        to={paths.makeStream(stream.id)}
        prefetch="intent"
        className={streamCardLink}
      >
        <div>
          <div className={streamCardHeader}>
            <div className={mobileHeaderMainContent}>
              <div className={categoryTag}>
                <span>{getStreamCategoryTitle(stream.streamCategory)}</span>
              </div>

              {isMyStreams ? (
                <StreamFollowers followersCount={stream.followersCount} />
              ) : null}
              <CopyButton
                className={copyIdButton}
                text={stream.id}
                title="copy id"
              />
            </div>

            {stream.isFeatured && <FeaturedBadge />}
          </div>
        </div>

        <div>
          <div className={stateContainer}>
            <p className={cardTitle}>{stream.name}</p>

            <StreamStateIndicator streamState={stream.streamState} />
          </div>

          <p className={cardDescription}>{stream.description}</p>
        </div>
        <div className={streamCardFooter}>
          {!isMyStreams ? (
            <div className={streamCardFooterAuditory}>
              <StreamAuthor
                author={stream.author}
                usernameContainerClassName={streamCardUsernameContainer}
              />
              <StreamFollowers followersCount={stream.followersCount} />
            </div>
          ) : null}
          <StreamPriceTag
            price={
              stream.paidData?.length
                ? convertCentsToDollars(getMonthlyPrice(stream.paidData) ?? 0)
                : undefined
            }
            className={streamTag}
            packageOnly={stream.packageOnly}
            data-testid={'streamCardPrice'}
          />
          {isMyStreams ? (
            <StreamStateTag
              state={stream.streamState}
              type={stream.streamType}
              className={streamTag}
            />
          ) : null}
          {stream.subscription?.active && stream.subscription.cancelAt && (
            <StreamAccessTill cancelAt={stream.subscription.cancelAt} />
          )}
        </div>
      </Link>
    </LayoutCard>
  );
};
